/**
 * orgTemplates.ts — the institution-type → whitelabel-template registry.
 *
 * A template is a PRESET BUNDLE over existing switches — routes, navigation
 * order, vocabulary, module flags — never a code fork. Every surface stays a
 * shared component, and the standing contracts (privacy, anti-vanity,
 * predicate-not-dossier, honest-null) are constitutional and identical across
 * templates. Adding a new institution type = adding one registry entry here;
 * nothing else in the schema changes.
 *
 * Render isolation contract: the whitelabel app builds its ROUTE TREE and
 * navigation from the resolved template. Surfaces not in the template are
 * never registered as routes and never mounted — they leave no trace in the
 * DOM, CSSOM, or network (lazy chunks for them are never fetched).
 */

export const INSTITUTION_TYPES = ["InstitutionAlumni", "MemberClub"] as const;
export type InstitutionType = (typeof INSTITUTION_TYPES)[number];

export type TemplateId = "institution" | "club";

/**
 * Surface keys the whitelabel app knows how to build. A template's `nav`
 * lists the main-navigation surfaces in display order; `modules` gates
 * surfaces/routes that exist outside the main nav.
 */
export type SurfaceKey =
  | "home"
  | "communities"
  | "events"
  | "directory"
  | "galleries"
  | "pass";

export interface VocabPack {
  /** Nav + page labels per surface (only surfaces the template uses matter). */
  surfaceLabels: Partial<Record<SurfaceKey, string>>;
  surfaceDescriptions: Partial<Record<SurfaceKey, string>>;
  /** What a member is called on this template. */
  memberNoun: string;
  memberNounPlural: string;
  /** The organization noun ("community" / "club"). */
  orgNoun: string;
}

export interface OrgTemplate {
  id: TemplateId;
  institutionType: InstitutionType;
  /** Staff-facing name + one-liner for the type selector. */
  label: string;
  description: string;
  /** Ordered main navigation. Surfaces not listed here and not enabled in
   *  `modules` are not registered as routes at all. */
  nav: SurfaceKey[];
  modules: {
    /** Member Pass surface (rotating presence pass + branded member ID card). */
    memberPass: boolean;
    /** Communities (threads/discussions) surfaces + routes. */
    communities: boolean;
    /** Photo galleries surface + route. */
    galleries: boolean;
  };
  vocab: VocabPack;
}

const INSTITUTION_TEMPLATE: OrgTemplate = {
  id: "institution",
  institutionType: "InstitutionAlumni",
  label: "Alumni institution",
  description:
    "The full community experience: Overview, Communities, Events, Directory, Galleries.",
  nav: ["home", "communities", "events", "directory", "galleries"],
  modules: {
    memberPass: false,
    communities: true,
    galleries: true,
  },
  vocab: {
    surfaceLabels: {
      home: "Overview",
      communities: "Communities",
      events: "Events",
      directory: "Directory",
      galleries: "Galleries",
    },
    surfaceDescriptions: {
      home: "Home",
      communities: "Join and participate in communities",
      events: "Upcoming events",
      directory: "Connect with community members",
      galleries: "Photos & media",
    },
    memberNoun: "member",
    memberNounPlural: "members",
    orgNoun: "community",
  },
};

const CLUB_TEMPLATE: OrgTemplate = {
  id: "club",
  institutionType: "MemberClub",
  label: "Members' club",
  description:
    "Pass-forward private club: Member Pass, The Club, Events, Members.",
  // Order = you → your club → what's on → who's here. nav[0] is also the default landing
  // surface (see defaultSurfacePath): a club opens to the Member Pass you show at the door.
  nav: ["pass", "home", "events", "directory"],
  modules: {
    memberPass: true,
    communities: false,
    galleries: false,
  },
  vocab: {
    surfaceLabels: {
      pass: "Member Pass",
      events: "Events",
      directory: "Members",
      home: "The Club",
    },
    surfaceDescriptions: {
      pass: "Your membership card and pass",
      events: "Upcoming club events",
      directory: "Fellow members",
      home: "News and notes from the club",
    },
    memberNoun: "member",
    memberNounPlural: "members",
    orgNoun: "club",
  },
};

const TEMPLATES: Record<InstitutionType, OrgTemplate> = {
  InstitutionAlumni: INSTITUTION_TEMPLATE,
  MemberClub: CLUB_TEMPLATE,
};

export function isInstitutionType(value: unknown): value is InstitutionType {
  return (
    typeof value === "string" &&
    (INSTITUTION_TYPES as readonly string[]).includes(value)
  );
}

/**
 * Resolve a tenant's template from its subdomain config. Tenants created
 * before institution types existed (or with an unknown/future type this build
 * doesn't know) get the alumni-institution template — the original whitelabel
 * experience — so existing subdomains are never affected.
 */
export function resolveOrgTemplate(config?: {
  institutionType?: string | null;
} | null): OrgTemplate {
  if (config && isInstitutionType(config.institutionType)) {
    return TEMPLATES[config.institutionType];
  }
  return INSTITUTION_TEMPLATE;
}

/** Route path for each template surface. */
export const SURFACE_PATH: Record<SurfaceKey, string> = {
  home: "/home",
  communities: "/communities",
  events: "/events",
  directory: "/directory",
  galleries: "/galleries",
  pass: "/pass",
};

/**
 * The surface a tenant opens to (index redirect AND post-login landing) — its first nav
 * item. Clubs lead with the Member Pass; the alumni template leads with home.
 */
export function defaultSurfacePath(template: OrgTemplate): string {
  return SURFACE_PATH[template.nav[0]] || "/home";
}

/** All templates, for staff-facing selectors. */
export function listOrgTemplates(): OrgTemplate[] {
  return INSTITUTION_TYPES.map((t) => TEMPLATES[t]);
}

/**
 * The tenant's organization id for org-aware surfaces (Growth Console,
 * org-scoped aggregates). A subdomain may serve a specific organization —
 * several orgs under one affiliation can each run a distinct whitelabel —
 * otherwise the affiliation IS the tenant (the platform's established
 * affiliation-as-tenant fallback).
 */
export function getTenantOrgId(config?: {
  organizationId?: string | null;
  affiliationId?: string | null;
} | null): string | null {
  return config?.organizationId || config?.affiliationId || null;
}

/**
 * Custom-font stylesheet URLs are restricted to known font CDNs — a stylesheet
 * link is an injection surface, so tenants may not point at arbitrary hosts.
 */
const FONT_CSS_HOSTS = ["fonts.googleapis.com", "fonts.bunny.net"];

export function isAllowedFontCssUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === "https:" && FONT_CSS_HOSTS.includes(u.hostname);
  } catch {
    return false;
  }
}
